import { AnalyticIdentifier } from '@common/neo.tax';
import { useModal } from '@ui-components/neo.tax';
import { MenuButton, useMenuState } from 'ariakit';
import { ReactElement, useCallback, useEffect, useState } from 'react';

import { useRdContext } from '@neo.tax/rd/context';
import { useRdProjectTeamMembers, RdProject, RdProjectTeamMember } from '@neo.tax/rd/hooks';
import {
  formatContributorNameWithEmail,
  useKeywordSearch,
  useAnalyticId,
  VirtualizedList,
  MenuItem,
  MenuItemDivider,
} from '@neo.tax/shared';

import { EditQreExpenseAllocationModalLoader } from '../qre-allocation/EditQreExpenseAllocationModalLoader';
import { AddPayrollExpenseMenu, ButtonStyled, KeywordSearchStyled } from './rd-project-team-members-toolbar.styled';

type AddPayrollExpenseButtonProps = {
  rdProject: RdProject;
};

export const AddPayrollExpenseButton = ({ rdProject }: AddPayrollExpenseButtonProps): ReactElement => {
  const menuState = useMenuState({ gutter: 1 });
  const addMemberBtnId = useAnalyticId(AnalyticIdentifier.ADD_TEAM_MEMBER_BUTTON);
  const memberInputId = useAnalyticId(AnalyticIdentifier.TEAM_MEMBER_SEARCH_INPUT);
  const { isOpen: isAllocationModalOpen, setIsOpen: setAllocationModalIsOpen } = useModal();
  const { searchKeyword, setSearchKeyword } = useKeywordSearch();
  const { fiscalYearId, periodName } = useRdContext();
  const {
    fetchMore,
    loading: loadingTeamMembers,
    rdProjectTeamMembers: teamMembers,
  } = useRdProjectTeamMembers({
    rdProjectId: rdProject.id,
    fiscalYearId,
    periodName,
    searchKeyword,
    hasAllocation: false,
    debounceKey: 'GET_PROJECT_TEAM_MEMBERS_WITHOUT_ALLOCATIONS_KEY',
    numElementsPerFetch: 30,
  });

  const [selectedRdExpenseId, setSelectedRdExpenseId] = useState<string | null>(null);

  const handleSelectRdExpense = useCallback(
    (rdExpenseId: string) => (): void => {
      setSelectedRdExpenseId(rdExpenseId);
      setAllocationModalIsOpen(true);
    },
    [setAllocationModalIsOpen],
  );

  const handleClose = (): void => {
    setSelectedRdExpenseId(null);
  };

  useEffect(() => {
    if (!menuState.open) {
      setSearchKeyword(null);
    }
  }, [menuState.open, setSearchKeyword]);

  const MemoizedRowComponent = useCallback(
    // eslint-disable-next-line react/display-name
    ({ item }: { index: number; item: RdProjectTeamMember }): ReactElement => (
      <MenuItem key={item.rdExpenseId} onClick={handleSelectRdExpense(item.rdExpenseId)}>
        {formatContributorNameWithEmail(item.contributorName, item.contributorEmail)}
      </MenuItem>
    ),
    [handleSelectRdExpense],
  );

  return (
    <>
      <ButtonStyled id={addMemberBtnId} as={MenuButton} state={menuState} size="lg" variant="primary" rounded="regular">
        Add...
      </ButtonStyled>
      <AddPayrollExpenseMenu state={menuState}>
        <KeywordSearchStyled
          searchKeyword={searchKeyword}
          setSearchKeyword={setSearchKeyword}
          disableClearSearch
          disableHighlight
          autoFocus
          id={memberInputId}
        />
        <MenuItemDivider />
        {(!teamMembers || !teamMembers.length) && (
          <MenuItem>
            <em>None</em>
          </MenuItem>
        )}
        <VirtualizedList
          data={teamMembers ?? []}
          isLoading={loadingTeamMembers}
          loadMore={fetchMore}
          MemoizedRowComponent={MemoizedRowComponent}
          // RED: the modal seems to be fixed to max height of 380 px, so be careful increasing this or you will get
          // double scroll bars that break the infinite scroll
          height={280}
          loadMoreRowFromBottomThreshold={15}
        />
      </AddPayrollExpenseMenu>
      {!!selectedRdExpenseId && (
        <EditQreExpenseAllocationModalLoader
          isOpen={isAllocationModalOpen}
          close={handleClose}
          rdExpenseId={selectedRdExpenseId}
          selectedRdProject={rdProject}
        />
      )}
    </>
  );
};
